import { useState, useEffect, useCallback, useRef } from 'react';
import tokenManager from '../../../../utils/tokenManager';

const BASE_URL = process.env.NODE_ENV === 'production'
  ? '' : (process.env.REACT_APP_BUSINESS_SERVICE_URL || 'http://localhost:5004');

export default function useMigrationSSE(migrationId) {
  const [status, setStatus] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  const [eta, setEta] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    if (!migrationId) return;

    const token = tokenManager.getToken();
    if (!token) {
      setError('No auth token available');
      return;
    }

    // Tracks unmount: EventSource callbacks fire asynchronously, and without
    // this guard a late message after unmount would setState on a dead hook.
    let isMounted = true;
    const safeSet = (fn) => { if (isMounted) fn(); };

    const url = `${BASE_URL}/api/business/email-migration/jobs/${migrationId}/stream?token=${token}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      safeSet(() => {
        setConnected(true);
        setError(null);
      });
    };

    es.onmessage = (event) => {
      if (!isMounted) return;
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'status':
            setStatus({
              progress: data.progress,
              completedUsers: data.completedUsers,
              totalUsers: data.totalUsers,
              transferredData: data.transferredData,
              jobStatus: data.jobStatus
            });
            break;

          case 'user_start':
            setUserProgress(prev => ({
              ...prev,
              [data.userId]: { status: 'running', folder: null, emailsProcessed: 0 }
            }));
            break;

          case 'user_complete':
            setUserProgress(prev => ({
              ...prev,
              [data.userId]: {
                status: 'completed',
                transferredBytes: data.transferredBytes,
                emailCount: data.emailCount
              }
            }));
            break;

          case 'user_error':
            setUserProgress(prev => ({
              ...prev,
              [data.userId]: { status: 'failed', error: data.error }
            }));
            break;

          case 'eta':
            setEta({
              estimatedSecondsRemaining: data.estimatedSecondsRemaining,
              avgBytesPerSecond: data.avgBytesPerSecond
            });
            break;

          case 'complete':
            setStatus(prev => ({ ...prev, jobStatus: 'completed', progress: 100 }));
            break;

          case 'failed':
            setStatus(prev => ({ ...prev, jobStatus: 'failed' }));
            setError(data.error);
            break;

          case 'cancelled':
            setStatus(prev => ({ ...prev, jobStatus: 'cancelled' }));
            break;

          default:
            break;
        }
      } catch (e) {
        // Ignore parse errors for heartbeats
      }
    };

    es.onerror = () => {
      // EventSource stays in CLOSED state after a fatal error but keeps the
      // underlying connection around; explicitly close to release it.
      if (es.readyState === EventSource.CLOSED) {
        es.close();
      }
      safeSet(() => {
        setConnected(false);
        setError('Connection lost, reconnecting...');
      });
    };

    return () => {
      isMounted = false;
      es.close();
      eventSourceRef.current = null;
    };
  }, [migrationId]);

  return { status, userProgress, eta, connected, error, disconnect };
}
